import {
  GetAccessTokenSilentlyType,
  GetAccessTokenWithPopupType,
  RecordingStatePayload,
  TranscriptionLiveEvent
} from '@/types/transcriptionSessionTypes';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

const authParams = {
  authorizationParams: {
    audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
  }
};

export const getLiveAccessToken = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup?: GetAccessTokenWithPopupType
): Promise<string> => {
  try {
    return await getAccessTokenSilently(authParams);
  } catch (err) {
    if (getAccessTokenWithPopup) {
      const token = await getAccessTokenWithPopup(authParams);
      if (token) return token;
    }
    throw err instanceof Error
      ? err
      : new Error('Unable to acquire access token for live stream.');
  }
};

export const subscribeToLiveStream = (
  sessionId: string,
  userId: string,
  accessToken: string,
  onEvent: (update: TranscriptionLiveEvent) => void,
  onError: (error: Error) => void,
  onOpen?: () => void
): (() => void) => {
  const url = new URL(
    `${API_BASE_URL}/transcriptionsessions/${sessionId}/live/stream`
  );
  url.searchParams.set('access_token', accessToken);
  url.searchParams.set('user_id', userId);

  const eventSource = new EventSource(url.toString());

  eventSource.onopen = () => {
    onOpen?.();
  };

  eventSource.onmessage = (event) => {
    try {
      const update = JSON.parse(event.data) as TranscriptionLiveEvent;
      onEvent(update);
    } catch (err) {
      console.error('Failed to parse live event payload:', err);
      onError(
        err instanceof Error
          ? err
          : new Error('Failed to parse live event payload')
      );
    }
  };

  eventSource.onerror = (event) => {
    console.error('Transcription live SSE error', event);
    onError(new Error('Connection to live updates failed'));
  };

  return () => {
    eventSource.close();
  };
};

export const postRecordingState = async (
  sessionId: string,
  payload: RecordingStatePayload,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup?: GetAccessTokenWithPopupType
): Promise<boolean> => {
  try {
    const token = await getLiveAccessToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const response = await fetch(
      `${API_BASE_URL}/transcriptionsessions/${sessionId}/live/state`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-User-Id': userId
        },
        body: JSON.stringify(payload)
      }
    );

    if (!response.ok) {
      console.warn(
        'Failed to post recording state',
        response.status,
        await response.text()
      );
    }
    return response.ok;
  } catch (error) {
    console.error('Error posting recording state:', error);
    return false;
  }
};

